import React from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { ChevronRight } from "lucide-react";

import { hasAdminAccess } from "../../../utils/roleAccess";
import { REPORT_BUTTONS, isReportOpen } from "../reportsCatalog";

/**
 * The Reports tab (Christian, Monday 13377706735): one page, five big blue
 * buttons, each opening a report that is already built. What each button opens
 * and who can open it lives in reportsCatalog.js.
 *
 * Blue rather than the CRM's teal because Christian asked for blue, and white
 * text on the teal falls well under readable contrast at this size. #1B6FA8
 * sits next to the teal in hue and keeps white text at 5.4:1.
 */
const REPORT_BLUE = "#1B6FA8";

const pillFor = (button, open) => {
  if (button.notBuilt) return "Coming soon";
  if (button.comingSoon) return open ? "Admin preview" : "Soon";
  return null;
};

const ReportButton = ({ button, open, onOpen }) => {
  const Icon = button.icon;
  const pill = pillFor(button, open);

  if (!open) {
    return (
      <button
        type="button"
        disabled
        aria-disabled="true"
        title={`${button.label} is coming soon`}
        className="w-full flex items-center gap-4 sm:gap-5 p-5 sm:p-6 rounded-2xl border border-[#D9E6F0] bg-[#EEF4F9] text-left cursor-not-allowed"
      >
        <span className="flex-shrink-0 w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-white flex items-center justify-center">
          <Icon className="w-6 h-6 sm:w-7 sm:h-7 text-[#5B7286]" aria-hidden="true" />
        </span>
        <span className="flex-1 min-w-0">
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="text-lg sm:text-xl font-semibold text-[#3F5568]">{button.label}</span>
            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide bg-amber-100 text-amber-700">
              {pill}
            </span>
          </span>
          <span className="block mt-1 text-sm sm:text-base font-normal text-[#5B7286]">{button.hint}</span>
        </span>
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={() => onOpen(button)}
      className="group w-full flex items-center gap-4 sm:gap-5 p-5 sm:p-6 rounded-2xl text-left text-white transition-[transform,box-shadow,background-color] duration-200 ease-out hover:-translate-y-0.5 hover:brightness-95 active:translate-y-0 motion-reduce:transition-none motion-reduce:hover:translate-y-0 focus:outline-none focus-visible:ring-4 focus-visible:ring-[#1B6FA8]/30 focus-visible:ring-offset-2"
      style={{
        backgroundColor: REPORT_BLUE,
        boxShadow: "0 8px 20px -10px rgba(27, 111, 168, 0.55)",
      }}
    >
      <span className="flex-shrink-0 w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-white/15 flex items-center justify-center">
        <Icon className="w-6 h-6 sm:w-7 sm:h-7" aria-hidden="true" />
      </span>
      <span className="flex-1 min-w-0">
        <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="text-lg sm:text-xl font-semibold">{button.label}</span>
          {pill && (
            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide bg-white/20 text-white">
              {pill}
            </span>
          )}
        </span>
        <span className="block mt-1 text-sm sm:text-base font-normal text-[#E3F0FA]">{button.hint}</span>
      </span>
      <ChevronRight
        className="flex-shrink-0 w-5 h-5 sm:w-6 sm:h-6 text-white/80 transition-transform duration-200 ease-out group-hover:translate-x-1 motion-reduce:transition-none"
        aria-hidden="true"
      />
    </button>
  );
};

const ReportsPage = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const isAdmin = hasAdminAccess(user);

  return (
    <div className="px-4 py-6 md:px-6 md:py-10 lg:py-14">
      {/* Centred and wide: Christian (1 Nov 2026) found the first version
          "weird being on the left" and "a bit small" on a desktop screen. */}
      <div className="max-w-4xl mx-auto">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 text-center">Reports</h1>
        <p className="text-sm md:text-base text-gray-500 mt-2 mb-6 md:mb-10 text-center">
          Pick the report you want to build for your vendor or landlord.
        </p>

        <ul className="space-y-3 md:space-y-4">
          {REPORT_BUTTONS.map((button) => (
            <li key={button.key}>
              <ReportButton
                button={button}
                open={isReportOpen(button, isAdmin)}
                onOpen={(b) => navigate(b.to)}
              />
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default ReportsPage;
